import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  ShieldAlert, Building2, ChevronRight, ChevronLeft, Search,
} from 'lucide-react'
import { schoolsApi, assessmentsApi } from '@/lib/api'
import { useAuthStore } from '@/store/auth'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { cn, verdictBadgeColor, verdictLabel, appCategoryLabel, formatDate } from '@/lib/utils'
import type { AssessmentListItem, DpoSchool } from '@/types'

const PAGE_SIZE = 20

// Filtri della catena di validazione: chiave URL → parametro API + etichetta
const CHAIN_FILTERS = [
  { key: 'tutte', chain: undefined, labelKey: 'dpo.filterAll' },
  { key: 'dpo_pending', chain: 'dpo_pending', labelKey: 'dpo.toValidateSection' },
  { key: 'handled', chain: 'handled', labelKey: 'dpo.handledSection' },
] as const

export default function DpoDashboard() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  // Scuola, filtro e pagina vivono nell'URL: tornando indietro dalla pagina
  // Valutazione si riapre la stessa vista (stessa scuola, filtro e pagina),
  // e la selezione sopravvive anche al refresh.
  const [searchParams, setSearchParams] = useSearchParams()
  const chainKey = searchParams.get('chain') ?? 'tutte'
  const page = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1)

  const setParams = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams)
    for (const [k, v] of Object.entries(updates)) {
      if (v === null) next.delete(k)
      else next.set(k, v)
    }
    setSearchParams(next)
  }

  // Ricerca con debounce: interroga il server quando l'utente smette di digitare
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim())
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput])
  useEffect(() => {
    // Nuova ricerca: si riparte dalla prima pagina
    if (searchParams.get('page')) setParams({ page: null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  // Validation backlog is compliance-critical and nothing invalidates these
  // caches when a DPO validates an assessment elsewhere in the app: every
  // visit to the Cruscotto must reread it rather than trust the app-wide
  // 2-minute cache (main.tsx).
  const { data: schools, isLoading } = useQuery({
    queryKey: ['dpo-schools'],
    queryFn: schoolsApi.dpoSchools,
    enabled: !!user,
    staleTime: 0,
  })

  const selected: DpoSchool | null =
    (schools ?? []).find((s) => s.id === searchParams.get('school')) ?? null

  const chain = CHAIN_FILTERS.find((f) => f.key === chainKey)?.chain

  const { data: assessments, isLoading: loadingAssessments } = useQuery({
    queryKey: ['dpo-school-assessments', selected?.id, chainKey, page, search],
    queryFn: () =>
      assessmentsApi.list({
        school_id: selected!.id,
        status: 'COMPLETED',
        page,
        page_size: PAGE_SIZE,
        search: search || undefined,
        chain,
      }),
    enabled: !!selected,
    staleTime: 0,
  })

  const rows = assessments ?? []
  // L'endpoint non restituisce il totale: il paginatore è Precedente/Successiva,
  // con Successiva disabilitata quando la pagina non è piena.
  const hasNext = rows.length === PAGE_SIZE
  const showPager = page > 1 || hasNext

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-mim-blue flex items-center justify-center">
          <ShieldAlert className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">{t('nav.dpoDashboard')}</h1>
          <p className="text-muted-foreground text-sm">
            {t('dpo.subtitle')}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array(2).fill(0).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      ) : !schools || schools.length === 0 ? (
        <Card>
          <CardContent className="pt-10 pb-10 text-center text-muted-foreground">
            <Building2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">{t('dpo.noneTitle')}</p>
            <p className="text-xs mt-1">
              {t('dpo.noneHint')}
            </p>
          </CardContent>
        </Card>
      ) : !selected ? (
        <div className="space-y-3">
          {schools.map((s) => (
            <Card key={s.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-4 pb-4">
                <button
                  type="button"
                  className="w-full flex items-center justify-between gap-4 text-left"
                  onClick={() => setSearchParams({ school: s.id })}
                >
                  <div className="min-w-0">
                    <p className="font-bold text-sm truncate">{s.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {[s.city, t('dpo.assessmentsCount', { count: s.total_assessments })].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {s.to_validate > 0 ? (
                      <Badge className="bg-giallo-dark text-white text-xs">
                        {t('dpo.toValidateBadge', { count: s.to_validate })}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs text-verde border-verde/40">
                        {t('dpo.noneWaiting')}
                      </Badge>
                    )}
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </button>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <button
              onClick={() => setSearchParams({})}
              className="text-xs text-mim-blue hover:underline"
            >
              ← {t('dpo.allSchools')}
            </button>
            <h2 className="text-lg font-bold mt-1">{selected.name}</h2>
          </div>

          {/* Filtri della catena + ricerca */}
          <div className="flex items-center gap-2 flex-wrap">
            {CHAIN_FILTERS.map(({ key, labelKey }) => (
              <button
                key={key}
                type="button"
                onClick={() => setParams({ chain: key === 'tutte' ? null : key, page: null })}
                className={cn(
                  'px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
                  chainKey === key
                    ? 'bg-mim-blue text-white border-mim-blue dark:bg-primary dark:border-primary dark:text-primary-foreground'
                    : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted'
                )}
              >
                {t(labelKey as any)}
                {key === 'dpo_pending' && selected.to_validate > 0 && ` (${selected.to_validate})`}
              </button>
            ))}
            <div className="relative ml-auto w-full sm:w-60">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('history.searchPlaceholder')}
                aria-label={t('history.searchAria')}
                className="pl-9 h-9"
              />
            </div>
          </div>

          <Card>
            <CardContent className="pt-4">
              {loadingAssessments ? (
                <div className="space-y-2">
                  {Array(5).fill(0).map((_, i) => <Skeleton key={i} className="h-11 rounded-lg" />)}
                </div>
              ) : rows.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-10">
                  {chainKey === 'dpo_pending' ? t('dpo.emptyToValidate') : t('dpo.emptyList')}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        {[
                          t('registro.headers.strumento'),
                          t('registro.headers.categoria'),
                          t('common.verdict'),
                          t('dpo.chainState'),
                          t('common.date'),
                          t('registro.headers.azioni'),
                        ].map((h) => (
                          <th key={h} className="text-left py-2 pr-4 font-semibold text-muted-foreground text-xs uppercase">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((item) => (
                        <AssessmentTableRow
                          key={item.id}
                          item={item}
                          onOpen={() => navigate(`/assessment/${item.id}`)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {showPager && !loadingAssessments && (
                <div className="flex items-center justify-between pt-4 mt-2 border-t border-border/60">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setParams({ page: page <= 2 ? null : String(page - 1) })}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    {t('common.previous')}
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    {t('common.page')} {page}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!hasNext}
                    onClick={() => setParams({ page: String(page + 1) })}
                  >
                    {t('common.next')}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}

function AssessmentTableRow({ item, onOpen }: { item: AssessmentListItem; onOpen: () => void }) {
  const { t, i18n } = useTranslation()
  const validated = !!item.dpo_validated_at
  const authorized = item.review_status === 'APPROVED' || item.review_status === 'OVERRIDDEN'
  const pendingDpo = item.review_status === 'PENDING' && !validated

  return (
    <tr className="border-b border-border/50 hover:bg-muted/30">
      <td className="py-2.5 pr-4 font-medium">{item.app.name}</td>
      <td className="py-2.5 pr-4 text-muted-foreground text-xs">
        {appCategoryLabel(item.app.category, i18n.language)}
      </td>
      <td className="py-2.5 pr-4">
        {item.verdict ? (
          <Badge className={cn('text-xs', verdictBadgeColor(item.verdict))}>
            {verdictLabel(item.verdict, i18n.language)}
          </Badge>
        ) : (
          '—'
        )}
      </td>
      <td className="py-2.5 pr-4">
        {authorized ? (
          <Badge className="bg-verde text-white text-xs">{t('dpo.authorized')}</Badge>
        ) : validated ? (
          <Badge className="bg-mim-blue text-white text-xs">{t('dpo.validatedWaiting')}</Badge>
        ) : item.review_status === 'PENDING' ? (
          <Badge className="bg-giallo-dark text-white text-xs">{t('dpo.rowToValidate')}</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">{t('dpo.notInChain')}</span>
        )}
      </td>
      <td className="py-2.5 pr-4 text-xs text-muted-foreground whitespace-nowrap">
        {formatDate(item.created_at, i18n.language)}
      </td>
      <td className="py-2.5">
        <Button
          size="sm"
          variant={pendingDpo ? 'default' : 'outline'}
          onClick={onOpen}
          className={cn('h-7 text-xs', pendingDpo && 'bg-mim-blue hover:bg-blue-800 text-white')}
        >
          {pendingDpo ? t('dpo.examine') : t('common.open')}
        </Button>
      </td>
    </tr>
  )
}
